import { X, ExternalLink, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface InvoicePdfModalProps {
  pdfUrl: string;
  invoiceNumber?: string | null;
  onClose: () => void;
}

/**
 * Inline iframe-based viewer for Intervend's fiscal invoice PDFs. The
 * pre-signed S3 URL drops straight into an iframe — browser PDF viewers
 * (Chrome/Firefox/Safari) handle rendering with no extra deps.
 */
export function InvoicePdfModal({ pdfUrl, invoiceNumber, onClose }: InvoicePdfModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/60 p-2 sm:p-6">
      <div className="relative flex w-full max-w-5xl flex-col rounded-xl bg-background shadow-2xl">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div className="min-w-0">
            <h2 className="font-semibold">Fiskalni račun</h2>
            {invoiceNumber && (
              <p className="text-xs text-muted-foreground font-mono truncate">
                {invoiceNumber}
              </p>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <Button
              size="sm"
              variant="outline"
              onClick={() => window.open(pdfUrl, '_blank', 'noopener,noreferrer')}
            >
              <ExternalLink className="w-4 h-4 mr-1.5" />
              <span className="hidden sm:inline">Otvori</span>
            </Button>
            <a
              href={pdfUrl}
              download={invoiceNumber ? `${invoiceNumber}.pdf` : 'invoice.pdf'}
              className="inline-flex h-9 items-center justify-center gap-1.5 rounded-md border border-input bg-background px-3 text-sm font-medium hover:bg-accent hover:text-accent-foreground"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Preuzmi</span>
            </a>
            <Button size="icon" variant="ghost" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>
        <iframe
          src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=1&zoom=100`}
          title="Fiscal invoice PDF"
          className="flex-1 w-full h-[80vh] rounded-b-xl"
        />
      </div>
    </div>
  );
}
